/* Preserve connected letterforms: one text run, revealed by sequential ink masks. */
if (!customElements.get('cb-signature')) {
  let sequence = 0;
  customElements.define('cb-signature', class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.dataset.animate === 'false') return;
      try {
        const loaded = await document.fonts.load('100px CBSignature', 'Catalin Botezatu');
        if (!loaded.length || !this.isConnected) return;
        const ns = 'http://www.w3.org/2000/svg';
        const make = (tag, attributes) => {
          const node = document.createElementNS(ns, tag);
          Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
          return node;
        };
        const svg = make('svg', {'aria-hidden':'true', focusable:'false', preserveAspectRatio:'none'});
        // John Bulgarry's ascent is 750/1000 and its line box is 1000/1000.
        // Match the fallback's baseline so starting the animation cannot move the word.
        const text = make('text', {x:0, y:75, fill:'currentColor', 'font-family':'CBSignature', 'font-size':100, 'font-weight':400, 'letter-spacing':0});
        text.textContent = 'Catalin Botezatu';
        svg.append(text);
        this.append(svg);
        const width = text.getComputedTextLength();
        if (!width) { svg.remove(); return; }
        // Handwritten glyphs can extend beyond their advance width, especially the final u.
        const ink = text.getBBox();
        const inkLeft = Math.min(-10, ink.x - 10);
        const inkRight = Math.max(width + 10, ink.x + ink.width + 12);
        const inkWidth = inkRight - inkLeft;
        const available = this.parentElement.clientWidth;
        const fontSize = parseFloat(getComputedStyle(this).fontSize);
        if (available && inkWidth / 100 * fontSize > available) {
          this.style.fontSize = `${available * 100 / inkWidth}px`;
        }
        svg.setAttribute('viewBox', `${inkLeft} 0 ${inkWidth} 100`);
        svg.style.width = `${inkWidth / 100}em`;
        svg.style.left = `${inkLeft / 100}em`;
        // This font has no Romanian glyphs. Add the two breve marks without
        // substituting mismatched letters from a fallback font.
        const accentedInk = make('g', {});
        text.replaceWith(accentedInk);
        accentedInk.append(text);
        for (const index of [1, 3]) {
          const extent = text.getExtentOfChar(index);
          const center = extent.x + extent.width * .55;
          const y = ink.y - 2;
          accentedInk.append(make('path', {d:`M ${center-5} ${y} Q ${center} ${y+7} ${center+5} ${y-1}`, fill:'none', stroke:'currentColor', 'stroke-width':1, 'stroke-linecap':'round'}));
        }
        const id = `cb-signature-ink-${++sequence}`;
        const defs = make('defs', {});
        const mask = make('mask', {id, maskUnits:'userSpaceOnUse', x:inkLeft, y:-100, width:inkWidth, height:300});
        let delay = .15;
        const durations = [.19,.09,.11,.08,.07,.1,.06,.2,.1,.09,.12,.08,.11,.09,.16];
        for (let i=0; i<text.textContent.length; i++) {
          const duration = durations[i] ?? .16;
          const left = i ? text.getSubStringLength(0,i) : inkLeft;
          const right = i === text.textContent.length-1 ? inkRight : text.getSubStringLength(0,i+1);
          const rect = make('rect', {x:left, y:-100, width:right-left+.2, height:300, fill:'white', class:'cb-signature__letter'});
          rect.style.setProperty('--letter-delay', `${delay}s`);
          rect.style.setProperty('--letter-duration', `${duration}s`);
          if (i === text.textContent.length - 1) {
            rect.addEventListener('animationend', () => accentedInk.removeAttribute('mask'), {once:true});
          }
          delay += duration * .9;
          mask.append(rect);
        }
        defs.append(mask);
        svg.prepend(defs);
        accentedInk.setAttribute('mask', `url(#${id})`);
        this.classList.add('is-ready');
        if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
          this.classList.add('is-writing');
        } else {
          this.observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
              // Join the hero timeline even when the font finishes loading late.
              const intro = this.closest('.cb-hero-signature')?.getAnimations?.()
                .find(animation => animation.animationName === 'cb-hero-enter');
              const remaining = intro
                ? Math.max(0, Number(intro.effect.getTiming().delay) + 200 - Number(intro.currentTime || 0))
                : 0;
              this.style.setProperty('--signature-intro-delay', `${remaining / 1000}s`);
              this.classList.add('is-writing');
              this.observer.disconnect();
            }
          }, {threshold:.35});
          this.observer.observe(this);
        }
      } catch (_) {
        this.querySelector('svg')?.remove();
        this.classList.remove('is-ready');
      }
    }
    replay() {
      if (!this.classList.contains('is-ready')) return;
      this.observer?.disconnect();
      this.querySelector('svg')?.remove();
      this.classList.remove('is-ready', 'is-writing');
      this.style.removeProperty('--signature-intro-delay');
      this.initialized = false;
      this.connectedCallback();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}
